import { Component, Tab, Label, DataSourceExecutionEvent } from "@mcleod/components";
import { CommonTooltips } from "@mcleod/common";
import { Alignment, ModelRow } from "@mcleod/core";

/**
 * Utility class containing common functionality shared between EdiBilling and ViewEdiBilling
 */
export class EdiBillingUtility {

    /**
     * Creates tooltip callbacks for EDI-specific components
     * @param component The component to attach the tooltip to
     * @param row The model row containing the data for the tooltip
     */
    static makeTooltipContent(component: Component, row: ModelRow): void {
        if (component === null || component === undefined || component.id === null || row === undefined || row === null) return;

        switch (component.id) {
            case "txtCorrectionIndicator":
                const correctionInd = row.get("correction_ind");
                const version = row.get("version");
                if (correctionInd) {
                    CommonTooltips.setTooltipContentLayout(
                        component,
                        null,
                        "lme/ar/CorrIndQuickInfo",
                        null,
                        { "version": version, "standard_code": correctionInd }
                    );
                    component.tooltipDynamicOptions = { position: Alignment.BOTTOM };
                }
                break;
            case "txtServiceCode":
                const serviceCode = row.get("service_code");
                const versionInput = row.get("version");
                if (serviceCode) {
                    CommonTooltips.setTooltipContentLayout(
                        component,
                        null,
                        "lme/ar/ServiceCodeQuickInfo",
                        null,
                        { "version_input": versionInput, "element_input": "56", "standard_code": serviceCode }
                    );
                    component.tooltipDynamicOptions = { position: Alignment.BOTTOM };
                }
                break;
        }
    }

    /**
     * Styles a tab with consistent appearance and adds a count label for error display
     * @param tab The tab to style
     */
    static styleTab(tab: Tab): void {
        if (tab.heading != null && tab.headingLabel != null) {
            tab.heading.color = "subtle.light";
            tab.headingLabel.setProps({ color: "default", fontBold: true, fillRow: false });

            if (tab["countLabel"] == null) {
                tab["countLabel"] = new Label({
                    fontBold: true,
                    text: "",
                    fillRow: true,
                    color: "primary",
                    rowBreak: false,
                    marginLeft: 5,
                    id: "labelErrorCount"
                });
                tab.heading.insert(tab["countLabel"], 1);
            }
        }
    }

    /**
     * Handles EDI map error data source execution event
     * Updates the error count in the tab label
     * @param event The data source execution event
     * @param tabErrors The tab to update with error count
     */
    static handleEdiMapErrorAfterExecution(event: DataSourceExecutionEvent, tabErrors: Tab): void {
        const errorCount = event.dataSource.data.length;
        if (tabErrors != null && tabErrors["countLabel"] != null) {
            tabErrors["countLabel"].text = errorCount;
            if (errorCount > 0) {
                // Check if there are any actual errors (error_type "E")
                const hasActualErrors = event.dataSource.data.some(row => row.get("error_type") === "E");

                if (hasActualErrors) {
                    // There are actual errors, use error color
                    tabErrors["countLabel"].color = "error";
                } else {
                    // Only warnings present, use warning color
                    tabErrors["countLabel"].color = "warning";
                }
            }
        }
    }

    /**
     * Sets up lookup model filter for service code search
     * @param event The lookup event
     * @param activeRow The active row containing version data
     */
    static setupServiceCodeLookupFilter(event: any, activeRow: ModelRow): void {
        const ediVersion = activeRow?.get("version", "004010");
        event.filter.version_input = ediVersion;
        event.filter.element_input = "56";
    }

    /**
     * Sets up lookup model filter for correction indicator search
     * @param event The lookup event
     * @param activeRow The active row containing version data
     */
    static setupCorrectionIndicatorLookupFilter(event: any, activeRow: ModelRow): void {
        const ediVersion = activeRow?.get("version", "004010");
        event.filter.version_input = ediVersion;
        event.filter.element_input = "202";
    }
}
